import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
	DEFAULT_TRANSACTION_PAGE_SIZE,
	INSIGHTS_FETCH_PAGE_SIZE,
	INSIGHTS_WINDOW_DAYS,
	type TransactionSortOption,
} from '@/app/my-wallet/utils/constants';
import {
	aggregateByServiceType,
	aggregateLast30Days,
	buildSparklineSeries,
	computeRunway,
	type DailyInOutPoint,
	daysAgoIso,
	type RunwaySummary,
	type ServiceBreakdownEntry,
	serviceTypeOptions,
	sortTransactions,
	transactionMatchesSearch,
} from '@/app/my-wallet/utils/transactions';
import { showToast } from '@/components/reusable/toast';
import { getUserData } from '@/constants/global';
import { useRightPanelContext } from '@/context/right-section';
import { handleError } from '@/helpers/data';
import {
	type IWalletTransaction,
	useGetClaimedCouponsMutation,
	useGetCurrentConversionRateQuery,
	useGetWalletDetailsQuery,
	useGetWalletTransactionHistoryMutation,
	useLazyGetWalletDetailsQuery,
	useVerifyPaymentMutation,
} from '@/store/api';
import { useEnsureWalletMutation, useGetUserByEmailMutation } from '@/store/api/core';
import type { IWalletResponse } from '@/store/api/wallet/wallets.api';

import { slipTitle } from '../right-panel/wallet-transaction/presentation';
import { useModal } from './modal';

export const TRANSACTION_TYPE = ['UNKNOWN', 'CREDIT', 'DEBIT', 'RESERVE', 'RELEASE', 'SETTLE'] as const;

/**
 * Credits below this threshold flip the nav-widget pill into "low balance" state
 * (warning rail + inline "Top up" CTA). Override at build time with
 * REACT_APP_LOW_BALANCE_THRESHOLD. Default tuned for the ~₦1/credit conversion
 * rate ranges typical for staging; tune if the production rate moves materially.
 */
export const LOW_BALANCE_THRESHOLD: number = (() => {
	const raw = process.env.REACT_APP_LOW_BALANCE_THRESHOLD;
	const parsed = raw ? Number(raw) : NaN;
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 5000;
})();

export interface ActiveFilterPill {
	id: string;
	label: string;
	onRemove: () => void;
}

interface DateRange {
	from?: string;
	to?: string;
}

const DEBOUNCE_MS = 250;

export const useWalletProps = () => {
	const rightPanel = useRightPanelContext();
	const [getUserByEmail, { data: userData, isLoading: userDataLoading }] = useGetUserByEmailMutation();
	const { user } = getUserData();
	const modalProps = useModal();

	const [triggerGetWallet, { data: walletDetails, isLoading: gettingWalletBalance }] = useLazyGetWalletDetailsQuery();
	const [triggerEnsureWallet, { isLoading: ensureWalletLoading }] = useEnsureWalletMutation();

	const { data: conversionRate } = useGetCurrentConversionRateQuery();
	const nairaPerCredit = conversionRate?.nairaPerCredit ?? 0;

	const [paymentStatus, setPaymentStatus] = useState<'success' | 'failure'>('success');
	const [paymentReference, setPaymentReference] = useState<string>('');

	const [triggerGetClaimedCoupons, { data: claimedCoupons }] = useGetClaimedCouponsMutation();
	const [verifyPayment, { data: paymentVerificationDetails }] = useVerifyPaymentMutation();
	const [searchParams] = useSearchParams();

	const handlePaymentComplete = (success: boolean, reference: string) => {
		setPaymentStatus(success ? 'success' : 'failure');
		setPaymentReference(reference);
	};

	useEffect(() => {
		const reference = searchParams.get('reference');
		if (!reference) return;
		verifyPayment({ reference })
			.unwrap()
			.then((result) => {
				handlePaymentComplete(result.isVerified, reference);
				modalProps.handleOpenModal('');
			})
			.catch((error) => {
				handleError(error);
				console.error('Payment verification error:', error);
			});
	}, []);

	useEffect(() => {
		const setup = async () => {
			try {
				const userRes = await getUserByEmail({ email: user?.Email ?? '' }).unwrap();
				if (userRes.data.walletId) {
					await triggerGetWallet({ walletId: userRes.data.walletId }).unwrap();
				}
			} catch (error) {
				handleError(error, () => {
					showToast('error', 'wallet Not found!');
				});
			}
		};
		setup();
	}, []);

	useEffect(() => {
		if (!userData?.data.walletId) return;
		triggerGetClaimedCoupons({ walletId: userData.data.walletId }).unwrap().catch(handleError);
	}, [userData]);

	const onRequestWallet = useCallback(async () => {
		try {
			await triggerEnsureWallet({ email: user?.Email ?? '' }).unwrap();
			showToast('success', 'Your wallet has been created.');
			await getUserByEmail({ email: user?.Email ?? '' }).unwrap();
		} catch (error) {
			handleError(error);
		}
	}, [user?.Email, triggerEnsureWallet, getUserByEmail]);

	const creditBalance = useMemo(() => {
		if (!walletDetails?.creditBuckets) return [0, 0];
		const buckets = walletDetails.creditBuckets;
		const generalBucket = buckets.find((b) => b.creditType === 'GENERAL');
		const bonusBucket = buckets.find((b) => b.creditType === 'BONUS');
		return [generalBucket?.currentBalance ?? 0, bonusBucket?.availableBalance ?? 0];
	}, [walletDetails]);

	const couponSummary = useMemo(() => {
		const items = (claimedCoupons?.items ?? []) as { expiresAt?: string | null; isUsed?: boolean }[];
		const now = Date.now();
		const active = items.filter((c) => {
			const expiresAt = c?.expiresAt ? new Date(c.expiresAt).getTime() : Number.POSITIVE_INFINITY;
			const isUsed = c?.isUsed === true;
			return !isUsed && (Number.isNaN(expiresAt) || expiresAt > now);
		});
		return { count: active.length, total: items.length };
	}, [claimedCoupons]);

	const openRewards = useCallback(() => {
		rightPanel.callSection({
			component: 'coupon-rewards',
			title: 'Rewards',
			action: 'view',
			data: { walletId: userData?.data.walletId },
			compact: true,
		});
	}, [userData, rightPanel]);

	const onBuyCredits = useCallback(
		(prefilledNaira?: number) => {
			rightPanel.callSection({
				component: 'wallet-topup',
				action: 'create',
				title: 'Top Up Your Wallet',
				data: {
					walletId: userData?.data.walletId,
					prefilledNaira,
				},
			});
		},
		[rightPanel, userData]
	);

	const viewTransaction = useCallback(
		// `initiatedBy` is the owner's ledger telling the slip who made it ("Support 2 – Chinelo Obi").
		(transaction: IWalletTransaction, initiatedBy?: string | null) => {
			rightPanel?.callSection({
				action: 'view',
				component: 'wallet-transaction',
				title: `Transaction Details - ${slipTitle(transaction)}`,
				data: { transaction, nairaPerCredit, initiatedBy: initiatedBy ?? null },
			});
		},
		[rightPanel, nairaPerCredit]
	);

	const refreshBalance = useCallback(() => {
		if (!userData?.data.walletId) return;
		triggerGetWallet({ walletId: userData.data.walletId }).unwrap().catch(handleError);
		triggerGetClaimedCoupons({ walletId: userData.data.walletId }).unwrap().catch(handleError);
	}, [userData, triggerGetWallet, triggerGetClaimedCoupons]);

	return {
		userData,
		userDataLoading,
		creditBalance,
		onRequestWallet,
		walletDetails,
		gettingWalletBalance,
		ensureWalletLoading,
		nairaPerCredit,
		claimedCoupons,
		couponSummary,
		openRewards,
		onBuyCredits,
		viewTransaction,
		refreshBalance,
		paymentVerificationDetails,
		paymentStatus,
		paymentReference,
		modalProps,
	};
};

export type WalletNavStatus = 'walletless' | 'loading' | 'error' | 'empty' | 'low' | 'healthy';

export interface WalletNavBalance {
	walletId: string | undefined;
	/** The wallet-service user GUID (AppUser.UserId), not the integer JWT "UserId" claim.
	 *  Needed as `createdByUserId` when initialising a payment. */
	userId: string | undefined;
	balance: number;
	available: number;
	pending: number;
	currency: string;
	buckets: IWalletResponse['creditBuckets'];
	nairaPerCredit: number;
	formattedNaira: string | null;
	formattedCredits: string;
	isLoading: boolean;
	isError: boolean;
	isWalletless: boolean;
	balanceDiscrepancy: boolean;
	lowBalance: boolean;
	isEmpty: boolean;
	status: WalletNavStatus;
	lastUpdatedAt: number | null;
	refetch: () => void;
}

const WALLET_NAV_POLL_MS = 120_000;

/**
 * Slim, header-friendly variant of useWalletProps. Resolves the active user's
 * walletId via getUserByEmail (one-shot mutation), then keeps the wallet balance
 * fresh via RTK Query polling + focus + reconnect. Designed for the nav-bar
 * widget so it can stay mounted across every route without dragging in the
 * right-panel callbacks, claimed-coupons fetch, or paystack verification side
 * effects that useWalletProps owns.
 */
export const useWalletNavBalance = (): WalletNavBalance => {
	const { user } = getUserData();
	const email = user?.Email;

	const [resolveUser, { data: userResponse, isLoading: userLoading, isError: userError }] = useGetUserByEmailMutation({
		fixedCacheKey: 'wallet-nav-user',
	});

	const resolvedOnceRef = useRef(false);
	useEffect(() => {
		if (!email || resolvedOnceRef.current) return;
		resolvedOnceRef.current = true;
		resolveUser({ email }).unwrap().catch(handleError);
	}, [email, resolveUser]);

	const rawWalletId = userResponse?.data?.walletId;
	const walletId = rawWalletId && rawWalletId !== 'null' ? rawWalletId : undefined;
	// Same "null"-the-string guard: api.core serialises an unset field as the literal
	// string, which is truthy and slips past a bare falsy check.
	const rawUserId = userResponse?.data?.userId;
	const userId = rawUserId && rawUserId !== 'null' ? rawUserId : undefined;

	const { data: conversionRate } = useGetCurrentConversionRateQuery();
	const nairaPerCredit = conversionRate?.nairaPerCredit ?? 0;

	const {
		data: walletDetails,
		isLoading: walletLoading,
		isFetching: walletFetching,
		isError: walletError,
		refetch: refetchWallet,
		fulfilledTimeStamp,
	} = useGetWalletDetailsQuery(
		{ walletId: walletId ?? '' },
		{
			skip: !walletId,
			pollingInterval: WALLET_NAV_POLL_MS,
			refetchOnFocus: true,
			refetchOnReconnect: true,
		}
	);

	const balance = walletDetails?.currentBalance ?? 0;
	const available = walletDetails?.availableBalance ?? 0;
	const pending = walletDetails?.pendingBalance ?? 0;
	const currency = walletDetails?.currency ?? 'NGN';
	const buckets = walletDetails?.creditBuckets ?? [];
	const balanceDiscrepancy = walletDetails ? !walletDetails.balanceVerificationPassed : false;

	const formattedCredits = balance.toLocaleString('en-NG');
	const formattedNaira = useMemo(() => {
		if (!Number.isFinite(balance)) return null;
		if (nairaPerCredit > 0) {
			const naira = balance * nairaPerCredit;
			return `₦${naira.toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;
		}
		// No conversion rate available — fall back to credit-count framing so the
		// pill never renders an unprefixed bare number that could be mistaken for
		// naira at a glance.
		return `${formattedCredits} cr`;
	}, [balance, nairaPerCredit, formattedCredits]);

	const isLoading = userLoading || (walletId ? walletLoading : false);
	const isWalletless = !!email && !userLoading && !userError && !walletId;
	const isError = userError || walletError;
	const lowBalance = !!walletId && balance > 0 && balance < LOW_BALANCE_THRESHOLD;
	const isEmpty = !!walletId && balance === 0 && !isLoading && !isError;

	const status: WalletNavStatus = (() => {
		if (isWalletless) return 'walletless';
		if (isLoading) return 'loading';
		if (isError) return 'error';
		if (isEmpty) return 'empty';
		if (lowBalance) return 'low';
		return 'healthy';
	})();

	const refetch = useCallback(() => {
		if (!walletId) return;
		// Avoid stacking refetches if a fetch is already in flight (popover open
		// while polling tick fires).
		if (walletFetching) return;
		refetchWallet();
	}, [walletId, walletFetching, refetchWallet]);

	return {
		walletId,
		userId,
		balance,
		available,
		pending,
		currency,
		buckets,
		nairaPerCredit,
		formattedNaira,
		formattedCredits,
		isLoading,
		isError,
		isWalletless,
		balanceDiscrepancy,
		lowBalance,
		isEmpty,
		status,
		lastUpdatedAt: fulfilledTimeStamp ?? null,
		refetch,
	};
};

interface UseWalletTransactionsControllerArgs {
	walletId?: string;
	currentBalance: number;
}

export interface WalletTransactionsController {
	pageNumber: number;
	pageSize: number;
	setPageSize: (size: number) => void;
	handlePagination: (selectedItem: { selected: number }) => void;
	typeFilter: number[];
	setTypeFilter: (next: number[]) => void;
	serviceFilter: string[];
	setServiceFilter: (next: string[]) => void;
	dateRange: DateRange;
	setDateRange: (next: DateRange) => void;
	searchValue: string;
	setSearchValue: (next: string) => void;
	sort: TransactionSortOption;
	setSort: (next: TransactionSortOption) => void;
	rawItems: IWalletTransaction[];
	visibleItems: IWalletTransaction[];
	totalCount: number;
	totalPages: number;
	hasNextPage: boolean;
	loading: boolean;
	tableLoading: boolean;
	insightsLoading: boolean;
	refresh: () => void;
	resetFilters: () => void;
	serviceOptions: { value: string; label: string }[];
	dailyInOutSeries: DailyInOutPoint[];
	serviceBreakdown: ServiceBreakdownEntry[];
	sparklineSeries: number[];
	runway: RunwaySummary;
	insightsTransactions: IWalletTransaction[];
	insightsTotalCount: number;
	insightsTruncated: boolean;
	isClientSorted: boolean;
	isClientFiltered: boolean;
	hasActiveFilters: boolean;
	activeFilterPills: ActiveFilterPill[];
}

export const useWalletTransactionsController = ({
	walletId,
	currentBalance,
}: UseWalletTransactionsControllerArgs): WalletTransactionsController => {
	/*
	 * Cache keys are scoped BY WALLET, not global.
	 *
	 * `fixedCacheKey` shares one mutation result across every component using that key, so the
	 * previous literal strings meant every caller of this controller read the same entry. That was
	 * harmless while the only caller was the signed-in user's own wallet. It is not harmless now
	 * that an admin can drive it with someone else's id: viewing user A's ledger and then opening
	 * My Wallet served A's transactions on your own page until the refetch landed — another
	 * person's money, on a screen that gives no reason to doubt it.
	 *
	 * Same shape as `peek-state-${code}` in CodePeekBody. The sibling statement-download hook
	 * already opts out of key sharing for the mirror-image reason.
	 */
	const cacheScope = walletId || 'none';
	const [getTransactionHistory, { data: transactionHistory, isLoading: tableLoading }] = useGetWalletTransactionHistoryMutation({
		fixedCacheKey: `wallet-transactions-page-${cacheScope}`,
	});
	const [getInsightsHistory, { data: insightsHistory, isLoading: insightsLoading }] = useGetWalletTransactionHistoryMutation({
		fixedCacheKey: `wallet-transactions-insights-${cacheScope}`,
	});

	const [pageNumber, setPageNumber] = useState(1);
	const [pageSize, setPageSizeInternal] = useState<number>(DEFAULT_TRANSACTION_PAGE_SIZE);
	const [typeFilter, setTypeFilterInternal] = useState<number[]>([]);
	const [serviceFilter, setServiceFilterInternal] = useState<string[]>([]);
	const [dateRange, setDateRangeInternal] = useState<DateRange>({});
	const [searchValueRaw, setSearchValue] = useState('');
	const [searchValue, setDebouncedSearch] = useState('');
	const [sort, setSort] = useState<TransactionSortOption>('date-desc');

	useEffect(() => {
		const handle = setTimeout(() => setDebouncedSearch(searchValueRaw), DEBOUNCE_MS);
		return () => clearTimeout(handle);
	}, [searchValueRaw]);

	const buildRequest = useCallback(
		(overrides: {
			pageNumber?: number;
			pageSize?: number;
			typeFilter?: number[];
			serviceFilter?: string[];
			dateRange?: DateRange;
		}) => {
			const effectiveType = overrides.typeFilter ?? typeFilter;
			const effectiveService = overrides.serviceFilter ?? serviceFilter;
			const effectiveRange = overrides.dateRange ?? dateRange;
			void effectiveType;
			return {
				walletId: walletId ?? '',
				pageNumber: overrides.pageNumber ?? pageNumber,
				pageSize: overrides.pageSize ?? pageSize,
				includeInternalTransactions: false,
				...(effectiveService.length === 1 ? { serviceType: effectiveService[0] } : {}),
				...(effectiveRange.from ? { fromDate: effectiveRange.from } : {}),
				...(effectiveRange.to ? { toDate: effectiveRange.to } : {}),
			};
		},
		[walletId, pageNumber, pageSize, typeFilter, serviceFilter, dateRange]
	);

	const fetchTablePage = useCallback(
		(overrides: Parameters<typeof buildRequest>[0]) => {
			if (!walletId) return Promise.resolve();
			return getTransactionHistory(buildRequest(overrides)).unwrap().catch(handleError);
		},
		[walletId, getTransactionHistory, buildRequest]
	);

	const fetchInsights = useCallback(() => {
		if (!walletId) return;
		getInsightsHistory({
			walletId,
			fromDate: daysAgoIso(INSIGHTS_WINDOW_DAYS),
			pageSize: INSIGHTS_FETCH_PAGE_SIZE,
			pageNumber: 1,
			includeInternalTransactions: false,
		})
			.unwrap()
			.catch(handleError);
	}, [walletId, getInsightsHistory]);

	const insightsLoadedRef = useRef<string | null>(null);
	useEffect(() => {
		if (!walletId) return;
		if (insightsLoadedRef.current === walletId) {
			fetchTablePage({ pageNumber: 1 });
			return;
		}
		insightsLoadedRef.current = walletId;
		fetchTablePage({ pageNumber: 1 });
		fetchInsights();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [walletId]);

	const setPageSize = useCallback(
		(size: number) => {
			setPageSizeInternal(size);
			setPageNumber(1);
			fetchTablePage({ pageNumber: 1, pageSize: size });
		},
		[fetchTablePage]
	);

	const handlePagination = useCallback(
		(selectedItem: { selected: number }) => {
			const next = selectedItem.selected + 1;
			setPageNumber(next);
			fetchTablePage({ pageNumber: next });
		},
		[fetchTablePage]
	);

	const setTypeFilter = useCallback(
		(next: number[]) => {
			setTypeFilterInternal(next);
			setPageNumber(1);
			fetchTablePage({ pageNumber: 1, typeFilter: next });
		},
		[fetchTablePage]
	);

	const setServiceFilter = useCallback(
		(next: string[]) => {
			setServiceFilterInternal(next);
			setPageNumber(1);
			fetchTablePage({ pageNumber: 1, serviceFilter: next });
		},
		[fetchTablePage]
	);

	const setDateRange = useCallback(
		(next: DateRange) => {
			setDateRangeInternal(next);
			setPageNumber(1);
			fetchTablePage({ pageNumber: 1, dateRange: next });
		},
		[fetchTablePage]
	);

	const refresh = useCallback(() => {
		fetchTablePage({ pageNumber });
		fetchInsights();
	}, [fetchTablePage, fetchInsights, pageNumber]);

	const resetFilters = useCallback(() => {
		setTypeFilterInternal([]);
		setServiceFilterInternal([]);
		setDateRangeInternal({});
		setSearchValue('');
		setDebouncedSearch('');
		setSort('date-desc');
		setPageNumber(1);
		fetchTablePage({ pageNumber: 1, typeFilter: [], serviceFilter: [], dateRange: {} });
	}, [fetchTablePage]);

	const rawItems = useMemo<IWalletTransaction[]>(() => transactionHistory?.items ?? [], [transactionHistory]);

	const visibleItems = useMemo(() => {
		const typed = typeFilter.length ? rawItems.filter((tx) => typeFilter.includes(tx.transactionType)) : rawItems;
		const multiServiceClient = serviceFilter.length > 1 ? typed.filter((tx) => serviceFilter.includes(tx.serviceType)) : typed;
		const searched = searchValue
			? multiServiceClient.filter((tx) => transactionMatchesSearch(tx, searchValue))
			: multiServiceClient;
		return sortTransactions(searched, sort);
	}, [rawItems, typeFilter, serviceFilter, searchValue, sort]);

	const insightsTransactions = useMemo<IWalletTransaction[]>(() => insightsHistory?.items ?? [], [insightsHistory]);

	const dailyInOutSeries = useMemo(() => aggregateLast30Days(insightsTransactions), [insightsTransactions]);
	const serviceBreakdown = useMemo(() => aggregateByServiceType(insightsTransactions, 5), [insightsTransactions]);
	const sparklineSeries = useMemo(() => buildSparklineSeries(insightsTransactions), [insightsTransactions]);
	const runway = useMemo(() => computeRunway(currentBalance, insightsTransactions), [currentBalance, insightsTransactions]);

	const serviceOptions = useMemo(() => {
		const combined = [...insightsTransactions, ...rawItems];
		return serviceTypeOptions(combined);
	}, [insightsTransactions, rawItems]);

	const insightsTotalCount = insightsHistory?.totalCount ?? 0;
	const insightsTruncated = insightsTotalCount > insightsTransactions.length;

	const isClientSorted = sort !== 'date-desc';
	const isClientFiltered = typeFilter.length > 0 || serviceFilter.length > 1 || !!searchValue;

	const hasActiveFilters =
		typeFilter.length > 0 ||
		serviceFilter.length > 0 ||
		!!dateRange.from ||
		!!dateRange.to ||
		!!searchValueRaw ||
		sort !== 'date-desc';

	const activeFilterPills = useMemo<ActiveFilterPill[]>(() => {
		const pills: ActiveFilterPill[] = [];

		typeFilter.forEach((idx) => {
			pills.push({
				id: `type-${idx}`,
				label: TRANSACTION_TYPE[idx] ?? `Type ${idx}`,
				onRemove: () => setTypeFilter(typeFilter.filter((v) => v !== idx)),
			});
		});

		serviceFilter.forEach((value) => {
			pills.push({
				id: `service-${value}`,
				label: value,
				onRemove: () => setServiceFilter(serviceFilter.filter((v) => v !== value)),
			});
		});

		if (dateRange.from) {
			pills.push({
				id: 'date-from',
				label: `From ${new Date(dateRange.from).toLocaleDateString('en-GB')}`,
				onRemove: () => setDateRange({ ...dateRange, from: undefined }),
			});
		}
		if (dateRange.to) {
			pills.push({
				id: 'date-to',
				label: `To ${new Date(dateRange.to).toLocaleDateString('en-GB')}`,
				onRemove: () => setDateRange({ ...dateRange, to: undefined }),
			});
		}

		if (searchValueRaw) {
			pills.push({
				id: 'search',
				label: `Search "${searchValueRaw}"`,
				onRemove: () => setSearchValue(''),
			});
		}

		if (sort !== 'date-desc') {
			const sortLabel: Record<TransactionSortOption, string> = {
				'date-desc': 'Newest first',
				'date-asc': 'Oldest first',
				'amount-desc': 'Highest amount',
				'amount-asc': 'Lowest amount',
			};
			pills.push({
				id: 'sort',
				label: `Sort: ${sortLabel[sort]}`,
				onRemove: () => setSort('date-desc'),
			});
		}

		return pills;
	}, [typeFilter, serviceFilter, dateRange, searchValueRaw, sort, setTypeFilter, setServiceFilter, setDateRange]);

	return {
		pageNumber,
		pageSize,
		setPageSize,
		handlePagination,
		typeFilter,
		setTypeFilter,
		serviceFilter,
		setServiceFilter,
		dateRange,
		setDateRange,
		searchValue: searchValueRaw,
		setSearchValue,
		sort,
		setSort,
		rawItems,
		visibleItems,
		totalCount: transactionHistory?.totalCount ?? 0,
		totalPages: transactionHistory?.totalPages ?? 0,
		hasNextPage: transactionHistory?.hasNextPage ?? false,
		loading: tableLoading || insightsLoading,
		tableLoading,
		insightsLoading,
		refresh,
		resetFilters,
		serviceOptions,
		dailyInOutSeries,
		serviceBreakdown,
		sparklineSeries,
		runway,
		insightsTransactions,
		insightsTotalCount,
		insightsTruncated,
		isClientSorted,
		isClientFiltered,
		hasActiveFilters,
		activeFilterPills,
	};
};
